import { memo, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { resetToastAlert } from 'services/toast-alert/toastAlertSlice';
import selectToastAlert from 'services/toast-alert/toastAlertSelectors';
import announceLayoutShift from 'utils/layoutShift';
import { QaCommonComponent } from 'testdata/constants';
import ToastAlert from './toast-alert';

function GlobalToastAlert() {
  const toastAlert = useAppSelector(selectToastAlert);
  const dispatch = useAppDispatch();

  /**
   * This toast renders in the sticky container above the whole layout
   * (`components/Layout/Layout.tsx`), in normal flow — so it moves the entire
   * page, sidebar included, when it appears and again when it goes. Anything
   * measuring the layout has to be told; see `utils/layoutShift`.
   *
   * BOTH edges matter, which is why this is an effect rather than a line in
   * `doResetToastAlert`. The appear edge is the one that bites: the toast is
   * usually raised by an async result (e.g. HealthLock activation failing) and
   * lands while the page is already settled. The disappear edge arrives here too
   * — `resetToastAlert` runs at the end of antd's close animation, and clearing
   * the slice is what unmounts this.
   *
   * `selectToastAlert` returns the slice object itself, so this dep is stable
   * for the toast's lifetime and the effect runs exactly once per edge, not on
   * every render. Cleanup cancels the loop so a second toast arriving mid-fade
   * does not leave two running.
   */
  useEffect(() => announceLayoutShift(), [toastAlert]);

  if (!toastAlert) {
    return null;
  }

  const doResetToastAlert = () => {
    dispatch(resetToastAlert());
  };

  return (
    <ToastAlert
      {...toastAlert}
      afterClose={doResetToastAlert}
      isGlobal
      qaSelector={QaCommonComponent.alertGlobalToast}
    />
  );
}

export default memo(GlobalToastAlert);
